/**
 * A [[LogSink]] implementation that logs to multiple sinks.
 */
export class TeeLogSink {
    constructor(sinks) {
        this._sinks = sinks;
    }
    log(level, context, ...args) {
        for (const logger of this._sinks) {
            logger.log(level, context, ...args);
        }
    }
    async flush() {
        await Promise.all(this._sinks.map(logger => logger.flush?.()));
    }
}
export class OptionalLoggerImpl {
    constructor(logSink, level = 'info', context) {
        this.debug = undefined;
        this.info = undefined;
        this.warn = undefined;
        this.error = undefined;
        const impl = (level) => (...args) => logSink.log(level, context, ...args);
        /* eslint-disable no-fallthrough , @typescript-eslint/ban-ts-comment */
        switch (level) {
            // @ts-ignore
            case 'debug':
                this.debug = impl('debug');
            // @ts-ignore
            case 'info':
                this.info = impl('info');
            // @ts-ignore
            case 'warn':
                this.warn = impl('warn');
            // @ts-ignore
            case 'error':
                this.error = impl('error');
        }
        /* eslint-enable @typescript-eslint/ban-ts-comment, no-fallthrough */
        this.flush = () => logSink.flush?.() ?? Promise.resolve();
    }
}
/**
 * Create a logger that will log to the console.
 */
export class ConsoleLogger extends OptionalLoggerImpl {
    constructor(level, context) {
        super(consoleLogSink, level, context);
    }
}
/**
 * An implementation of [[LogSink]] that logs using `console.log` etc
 */
export const consoleLogSink = {
    log(level, context, ...args) {
        console[level](...stringified(context), ...args.map(normalizeArgument));
    },
};
/**
 * A console logger that enables the caller to format log lines.
 */
export class FormatLogger {
    constructor(format) {
        this._format = format;
    }
    log(level, context, ...args) {
        console[level](...this._format(level, ...stringified(context), ...args));
    }
}
/**
 * A console logger that prefixes log lines with a log level.
 */
export const nodeConsoleLogSink = {
    log(level, context, ...args) {
        console[level](logLevelPrefix[level], ...stringified(context), ...args.map(normalizeArgument));
    },
};
/**
 * Log line level prefixes, used by the node logger. They are uniform length
 * to make visual parsing of a log file easier.
 */
export const logLevelPrefix = {
    error: 'ERR',
    warn: 'WRN',
    info: 'INF',
    debug: 'DBG',
};
/**
 * A logger that logs nothing.
 */
export class SilentLogger {
}
/**
 * The LogContext facilitates constructing and adding to the
 * Context passed to the LogSink.
 *
 * Typical usage is something like:
 *
 *   const lc = new LogContext('debug');
 *   lc.debug?.('hello');
 *   const lc2 = lc.withContext('foo');
 *   f(lc2);  // logging inside f will contain 'foo' in the Context.
 */
export class LogContext extends OptionalLoggerImpl {
    constructor(level = 'info', context, logSink = consoleLogSink) {
        super(logSink, level, context);
        this._level = level;
        this._logSink = logSink;
        this._context = context;
    }
    /**
     * Creates a new Logger that with the given key and value
     * added to the logged Context.
     */
    withContext(key, value) {
        const ctx = { ...this._context, [key]: value };
        return new LogContext(this._level, ctx, this._logSink);
    }
}
function stringified(context) {
    const args = [];
    for (const [k, v] of Object.entries(context ?? {})) {
        const arg = v === undefined ? k : `${k}=${v}`;
        args.push(arg);
    }
    return args;
}
function normalizeArgument(v) {
    switch (typeof v) {
        case 'string':
        case 'number':
        case 'boolean':
        case 'undefined':
        case 'symbol':
        case 'bigint':
            return v;
        case 'object':
            if (v === null) {
                return null;
            }
            break;
    }
    return JSON.stringify(v, errorReplacer);
}
function errorReplacer(_key, v) {
    if (v instanceof Error) {
        return {
            name: v.name,
            message: v.message,
            stack: v.stack,
            ...('cause' in v ? { cause: v.cause } : null),
        };
    }
    return v;
}
